import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { captureError } from '@/lib/sentry';
import { queryKeys } from '@/lib/query-keys';
import {
  AcceptConsentInputSchema,
  AcceptConsentResponseSchema,
  PendingConsentsResponseSchema,
  type AcceptConsentInput,
  type PendingConsent,
} from '@/types/legal-consent';

export type { PendingConsent, AcceptConsentInput } from '@/types/legal-consent';

export function usePendingConsents() {
  const { user, loading: authLoading } = useAuth();

  const query = useQuery({
    queryKey: queryKeys.consents.pending(user?.id),
    queryFn: async (): Promise<PendingConsent[]> => {
      const { data, error } = await supabase.functions.invoke('get-pending-consents', {
        body: {},
      });
      if (error) throw error;
      // Runtime-validate the edge function's response. Without this, a server
      // returning `{ error: '...' }` with HTTP 200 (which functions.invoke
      // doesn't flag) would yield `pending: []` and silently hide the modal —
      // bypassing the legally-required consent gate.
      const parsed = PendingConsentsResponseSchema.safeParse(data);
      if (!parsed.success) {
        throw new Error(`Malformed get-pending-consents response: ${parsed.error.message}`);
      }
      return parsed.data.pending;
    },
    enabled: !!user && !authLoading,
    // Refetch on focus catches the "accepted in another tab" case. We bump
    // staleTime to 15 minutes so most tab-switches don't trigger a network
    // round-trip — content can be sizable (markdown for every pending doc).
    refetchOnWindowFocus: true,
    staleTime: 15 * 60_000,
  });

  // Surface read-path failures to GlitchTip. TanStack Query swallows errors
  // by default; the consumer (ConsentModal) renders an error state when
  // `query.error` is set so the user can't bypass consent silently.
  useEffect(() => {
    if (query.error) {
      captureError(query.error as Error, { feature: 'pending-consents-fetch' });
    }
  }, [query.error]);

  return query;
}

export function useAcceptConsent() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: AcceptConsentInput) => {
      // Validate at the boundary — same schema the edge function will parse.
      // Discriminated union means TS already prevents most malformed payloads,
      // but a runtime safety net costs nothing here.
      const parsedInput = AcceptConsentInputSchema.parse(input);
      const { data, error } = await supabase.functions.invoke('accept-consent', {
        body: parsedInput,
      });
      if (error) throw error;
      const parsed = AcceptConsentResponseSchema.safeParse(data);
      if (!parsed.success) {
        throw new Error(`Malformed accept-consent response for version_id ${parsedInput.version_id}`);
      }
      return parsed.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.consents.pending(user?.id) });
    },
    onError: (err, input) => {
      captureError(err as Error, {
        feature: 'accept-consent',
        version_id: input.version_id,
      });
      toast({
        title: 'Error',
        description: 'Could not record your acceptance. Please try again.',
        variant: 'destructive',
      });
    },
  });
}
